import { logError } from 'fergy-core-react-logging';
import { forwardRef, useState } from 'react';
import { type FieldError, type FieldErrors } from 'react-hook-form';
import { type AnalyticsEvent, AnalyticsHelper } from '../../../../helpers/analytics/analytics.helper';
import { generateDataSelector } from '../../../../helpers/general-helper/general-helper';
import { CharacterCount } from '../../../common-components/character-count/character-count.component';
import { InputMessage } from '../../../common-components/input-message/input-message.component';
import { INPUT_SIZE_MAP, type InputMode, type InputSize } from '../input.types';

export type BaseTextInputProps = {
	autoCapitalize?: string;
	autoCorrect?: string;
	autoComplete?: string;
	automationHook?: string;
	analyticsHook?: string;
	className?: string; // classes for the container around the input
	defaultValue?: string;
	description?: string;
	disabled?: boolean;
	icon?: JSX.Element;
	id?: string;
	inputClassName?: string; // classes for the input element
	inputMode?: InputMode;
	invalid?: boolean;
	invalidMessage?: string | FieldError | FieldErrors;
	maxLength?: number;
	max?: string | number;
	message?: JSX.Element;
	min?: string | number;
	name?: string;
	onBlur?: (data: React.FocusEvent<HTMLInputElement>) => void;
	omitBaseClasses?: boolean;
	onChange?: (data: React.ChangeEvent<HTMLInputElement>) => void;
	onFocus?: (data: React.FocusEvent<HTMLInputElement>) => void;
	onKeyDown?: (data: React.KeyboardEvent<HTMLInputElement>) => void;
	onSubmit?: (data: React.FormEvent<HTMLElement>) => void;
	pattern?: string;
	placeholder?: string;
	required?: boolean;
	step?: number;
	testId?: string;
	type?: string;
	ariaLabel?: string;
	value?: string | number;
	// Analytics data that will sent upon clicking or focus into the input.
	analyticsEvent?: AnalyticsEvent;
	readOnly?: boolean;
	borderDark?: boolean;
	size?: InputSize;
	leading?: JSX.Element;
	trailing?: JSX.Element;
	includeCounter?: boolean;
	maskContent?: boolean;
};

export const BaseTextInput = forwardRef<HTMLInputElement, BaseTextInputProps>(
	(
		{
			className = '',
			description,
			icon,
			id,
			invalid,
			invalidMessage,
			omitBaseClasses,
			required = false,
			testId,
			type = 'text',
			automationHook,
			analyticsHook,
			analyticsEvent,
			onFocus,
			borderDark,
			size = 'DEFAULT',
			leading,
			trailing,
			ariaLabel,
			inputClassName = 'ph3',
			includeCounter = false,
			value,
			maxLength,
			maskContent,
			disabled = false,
			...remainingProps
		},
		ref
	) => {
		const [currentCount, setCurrentCount] = useState(value?.toString().length || 0);

		let containerClasses = `theme-luxury:input! ${
			disabled ? 'theme-luxury:bg-disabled! theme-luxury:cursor-not-allowed! theme-luxury:border-disabled!' : ''
		} input-reset br2 ba w-100 truncate flex items-stretch ${className}`.trim();
		if (invalid && !omitBaseClasses) {
			containerClasses = `${containerClasses} b--theme-error invalid-error theme-luxury:shadow-error!`;
		} else if (!omitBaseClasses) {
			containerClasses = `${containerClasses} theme-grey-darker ${borderDark ? 'b--theme-grey-darker' : 'b--theme-grey-light'}`;
		} else {
			containerClasses = className;
		}

		const focusHandler = analyticsEvent
			? (e: React.FocusEvent<HTMLInputElement>) => {
					if (onFocus) {
						onFocus(e);
					}
					AnalyticsHelper.track(analyticsEvent.data).catch(logError);
			  }
			: onFocus;

		return (
			<div className="flex flex-column">
				{/* tslint:disable-next-line: react-a11y-required  We are abiding by this rule at runtime */}
				<div className={containerClasses} data-testid="input-container">
					{leading}
					<input
						id={id}
						className={`truncate bw0 w-100 color-inherit ${inputClassName} ${INPUT_SIZE_MAP[size]} theme-luxury:border-none! theme-luxury:shadow-none! theme-luxury:bg-transparent!`}
						type={type}
						aria-required={required}
						required={required}
						data-testid={testId}
						data-automation={generateDataSelector('input', automationHook)}
						data-analytics={generateDataSelector('input', analyticsHook)}
						onFocus={focusHandler}
						ref={ref}
						aria-label={ariaLabel}
						onInput={(e) => setCurrentCount(e.currentTarget.value.length)}
						value={value}
						maxLength={maxLength}
						data-cs-mask={maskContent}
						disabled={disabled}
						{...remainingProps}
					/>
					{icon}
					{trailing}
				</div>
				{(includeCounter || invalid || description) && (
					<div className="flex justify-between w-auto">
						<InputMessage description={description} invalid={invalid} invalidMessage={invalidMessage} />
						{includeCounter && (
							<CharacterCount testId={testId} invalid={invalid} currentCount={currentCount} maxLength={maxLength} />
						)}
					</div>
				)}
			</div>
		);
	}
);
